/**
 * Server status fetch with fallback.
 * Endpoints: /api/server-status.json (404) -> /api/server/status (200)
 * Also merges: /api/services/status, /api/connections/active
 */

export interface ServerStatusData {
  cpu: string;
  memory: string;
  disk: string;
  uptime: string;
  nginx_status?: string;
  matrix_status?: string;
  api_status?: string;
  active_connections?: string;
  last_updated?: string;
}

function fmt(v: unknown, unit = ""): string {
  if (v == null || v === "N/A") return "N/A";
  if (typeof v === "object" && "value" in (v as object)) {
    const obj = v as { value?: unknown; unit?: string };
    return obj.value != null ? `${obj.value}${obj.unit || unit}` : "N/A";
  }
  return `${v}${unit}`;
}

export function normalizeServerStatus(
  raw: unknown,
  services?: Record<string, string> | null,
  connections?: { total?: number } | null
): ServerStatusData {
  const def: ServerStatusData = {
    cpu: "N/A",
    memory: "N/A",
    disk: "N/A",
    uptime: "N/A",
    nginx_status: "nepoznat",
    matrix_status: "nepoznat",
    api_status: "nepoznat",
    active_connections: "0",
  };

  if (raw && typeof raw === "object") {
    const r = raw as Record<string, unknown>;
    if (r.cpu && typeof r.cpu === "object") {
      def.cpu = fmt(r.cpu, "%");
      def.memory = fmt(r.memory, "%");
      def.disk = fmt(r.disk, "%");
      def.uptime = (r.uptime as string) || def.uptime;
    } else {
      def.cpu = (r.cpu as string) ?? def.cpu;
      def.memory = (r.memory as string) ?? def.memory;
      def.disk = (r.disk as string) ?? def.disk;
      def.uptime = (r.uptime as string) ?? def.uptime;
    }
    def.nginx_status = (r.nginx_status as string) ?? def.nginx_status;
    def.matrix_status = (r.matrix_status as string) ?? def.matrix_status;
    def.api_status = (r.api_status as string) ?? def.api_status;
    def.active_connections = (r.active_connections as string) ?? def.active_connections;
    def.last_updated = r.last_updated as string | undefined;
  }

  if (services) {
    def.nginx_status = services.nginx ?? def.nginx_status;
    def.matrix_status = services.matrix ?? def.matrix_status;
    def.api_status = services.api ?? def.api_status;
  }

  if (connections != null && typeof connections.total === "number") {
    def.active_connections = String(connections.total);
  }

  return def;
}

const CANDIDATES = [
  "/api/server-status.json",
  "/api/server/status",
];

export async function fetchServerStatus(): Promise<ServerStatusData> {
  let raw: unknown = null;
  let usedEndpoint = "";

  for (const url of CANDIDATES) {
    try {
      const r = await fetch(url + (url.includes("?") ? "&" : "?") + "_=" + Date.now());
      if (!r.ok) continue;
      raw = await r.json();
      usedEndpoint = url;
      break;
    } catch {
      continue;
    }
  }

  if (!raw) {
    console.log("[server-status] Svi endpointi failali:", CANDIDATES.join(", "));
    throw new Error("All server-status endpoints failed");
  }

  let services: Record<string, string> | null = null;
  let connections: { total?: number } | null = null;

  try {
    const r = await fetch("/api/services/status?_=" + Date.now());
    if (r.ok) services = await r.json();
  } catch {
    /* ignore */
  }

  try {
    const r = await fetch("/api/connections/active?_=" + Date.now());
    if (r.ok) connections = await r.json();
  } catch {
    /* ignore */
  }

  return normalizeServerStatus(raw, services, connections);
}
